import { useState, useCallback } from 'react';

export const useRemediation = () => {
    const [remediationWidget, setRemediationWidget] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Triggers the AI generation process.
     * @param {number} questionId - The ID of the current question
     * @param {number} conceptId - The ID of the concept/skill being tested
     * @param {string} studentWrongAnswer - The actual wrong text the student entered
     * @param {string} questionAnswerType - The type (RADIO, CHECKBOX, TEXTFIELD)
     */
    const triggerRemediation = useCallback(async (questionId, conceptId, studentWrongAnswer, questionAnswerType) => {
        setIsGenerating(true);
        setError(null);
        setRemediationWidget(null);

        let formattedAnswer = studentWrongAnswer;

        // Handle empty submissions
        if (formattedAnswer === 'None entered') {
            formattedAnswer = undefined;
        }
        // Format based on questionAnswerType
        else if (formattedAnswer) {
            if (questionAnswerType === 'RADIO') {
                formattedAnswer = `radio-${formattedAnswer.toLowerCase()}`;
            } else if (questionAnswerType === 'CHECKBOX') {
                // If it's a single string "A", convert to "checkbox-a"
                // If it's multiple (e.g., "A and B"), your formatUserAnswer might need splitting
                // This example assumes a single letter or logic to handle strings
                formattedAnswer = formattedAnswer.toLowerCase()
                    .split(' and ') // Split if formatUserAnswer joined them with ' and '
                    .map(item => `checkbox-${item.trim()}`)
                    .join('|');
            } else if (questionAnswerType === 'TEXTFIELD') {
                // Prepend nothing as requested
                formattedAnswer = studentWrongAnswer;
            }
        }

        try {
            const response = await fetch('/web/ui/visualisation/remediation', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    questionId,
                    conceptId,
                    studentWrongAnswer: formattedAnswer
                }),
            });

            if (!response.ok) {
                let errorMessage = `Server error: ${response.status}`;
                try {
                    const errorData = await response.json();
                    if (errorData.message) errorMessage = errorData.message;
                } catch (e) { }
                throw new Error(errorMessage);
            }

            const widgetJson = await response.json();
            setRemediationWidget(widgetJson);

        } catch (err) {
            console.error("Failed to generate remediation widget:", err);
            setError(err.message);
        } finally {
            setIsGenerating(false);
        }
    }, []);

    const clearRemediation = useCallback(() => {
        setRemediationWidget(null);
        setError(null);
    }, []);

    return {
        remediationWidget, // The JSON to pass to <VisualisationWidget />
        isGenerating,      // True while AI is thinking (show a spinner)
        error,             // If generation fails
        triggerRemediation,// Function to call on wrong answer
        clearRemediation   // Function to reset when moving to next question
    };
};